import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import apiService from '@/services/api'
import type { RelatedFileEntry } from '@/types'
import { GRAPH_FETCH_LIMIT, MAX_GRAPH_NODES } from '@/constants/fileLinks'
import {
  EMPTY_GRAPH,
  applyRelatedOverlay,
  buildInitialGraph,
  mergeGraphResponse,
  type GraphData,
} from './graphTransforms'

interface UseLinkGraphOptions {
  centerId: number
  relatedItems: RelatedFileEntry[]
  /** Only fetch while the graph is actually on screen. */
  enabled: boolean
}

export interface UseLinkGraphResult {
  data: GraphData
  /** Explicit-link edge count, ignoring suggestions. */
  explicitEdgeCount: number
  loading: boolean
  error: boolean
  /** The server hit its node cap on some fetch. */
  truncated: boolean
  /** The client-side node cap blocked at least one expansion. */
  hitCap: boolean
  expanding: number | null
  expand: (nodeId: number) => Promise<void>
  reload: () => Promise<void>
}

export function useLinkGraph({ centerId, relatedItems, enabled }: UseLinkGraphOptions): UseLinkGraphResult {
  const [base, setBase] = useState<GraphData>(EMPTY_GRAPH)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const [hitCap, setHitCap] = useState(false)
  const [expanding, setExpanding] = useState<number | null>(null)
  const loadedFor = useRef<number | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await apiService.getFileGraph(centerId, { depth: 1, limit: GRAPH_FETCH_LIMIT })
      setBase(buildInitialGraph(centerId, res))
      setTruncated(res.truncated)
      setHitCap(false)
      loadedFor.current = centerId
    } catch {
      setError(true)
      setBase(EMPTY_GRAPH)
    } finally {
      setLoading(false)
    }
  }, [centerId])

  useEffect(() => {
    if (loadedFor.current !== centerId) {
      setBase(EMPTY_GRAPH)
      setTruncated(false)
      setHitCap(false)
      setError(false)
    }
    if (enabled && loadedFor.current !== centerId) {
      void reload()
    }
  }, [centerId, enabled, reload])

  const expand = useCallback(
    async (nodeId: number) => {
      const node = base.nodes.find((n) => n.id === nodeId)
      if (!node || node.expanded || expanding !== null) return
      setExpanding(nodeId)
      try {
        const res = await apiService.getFileGraph(nodeId, { depth: 1, limit: GRAPH_FETCH_LIMIT })
        setBase((prev) => {
          const merged = mergeGraphResponse(prev, res, nodeId, MAX_GRAPH_NODES)
          if (merged.hitCap) setHitCap(true)
          return merged.data
        })
        if (res.truncated) setTruncated(true)
      } catch {
        setError(true)
      } finally {
        setExpanding(null)
      }
    },
    [base.nodes, expanding]
  )

  const data = useMemo(
    () => applyRelatedOverlay(base, centerId, relatedItems, MAX_GRAPH_NODES),
    [base, centerId, relatedItems]
  )

  const explicitEdgeCount = useMemo(() => base.links.filter((l) => l.kind === 'link').length, [base.links])

  return { data, explicitEdgeCount, loading, error, truncated, hitCap, expanding, expand, reload }
}
